import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Users, Calendar, DollarSign, Sparkles } from "lucide-react";
import { api } from "../lib/api";

/**
 * CustomerPlanDetail — read-only deep view of a single AI Concierge plan.
 * Routed from the "View Details" CTA on each RecentPlanCard.
 *
 * Renders the AI-generated itinerary, providers needed, estimated cost
 * breakdown, and a primary "Find a Concierge" CTA so the customer can move
 * forward and request that a verified TREL Concierge handle it.
 */
export default function CustomerPlanDetail() {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/ai/plans`)
      .then((r) => {
        if (!alive) return;
        const rows = r.data;
        const match = Array.isArray(rows) ? rows.find((p) => p.plan_id === planId) : null;
        if (!match) setErr("Plan not found.");
        setPlan(match || null);
      })
      .catch(() => alive && setErr("Could not load plan."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [planId]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-stone-500">Loading plan…</div>;
  }
  if (err || !plan) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/customer" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
          <ArrowLeft size={14}/> Back to dashboard
        </Link>
        <p className="mt-6 text-stone-700">{err || "Plan not found."}</p>
      </div>
    );
  }

  const body = plan.plan || {};
  const itinerary = Array.isArray(body.itinerary) ? body.itinerary : [];
  const needs = Array.isArray(body.providers_needed) ? body.providers_needed : [];
  const costs = Array.isArray(body.estimated_costs) ? body.estimated_costs : [];

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 pb-28 lg:pb-10" data-testid="customer-plan-detail">
      <Link to="/customer" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
        <ArrowLeft size={14}/> Back to dashboard
      </Link>

      <div className="mt-6 rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <Sparkles size={22} className="mt-1 text-amber-600"/>
          <div>
            <h1 className="font-serif text-3xl tracking-tight text-stone-900">{body.title || "Untitled plan"}</h1>
            {body.summary && <p className="mt-2 text-stone-600 leading-relaxed">{body.summary}</p>}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {body.duration && (
            <MetaTile icon={Calendar} label="Duration" value={body.duration}/>
          )}
          {body.guests != null && (
            <MetaTile icon={Users} label="Guests" value={`${body.guests}`}/>
          )}
          {body.estimated_price != null && (
            <MetaTile icon={DollarSign} label="Estimated" value={`$${Number(body.estimated_price).toLocaleString()}`}/>
          )}
          {Array.isArray(body.locations) && body.locations[0] && (
            <MetaTile icon={MapPin} label="Where" value={body.locations[0]}/>
          )}
        </div>
      </div>

      {itinerary.length > 0 && (
        <section className="mt-6 rounded-3xl border border-stone-200 bg-white p-6">
          <h2 className="font-serif text-xl">Itinerary</h2>
          <ol className="mt-4 space-y-4">
            {itinerary.map((it, idx) => (
              <li key={idx} className="border-l-2 border-amber-300 pl-4">
                <div className="text-[12px] uppercase tracking-wide text-amber-700 font-semibold">{it.time}</div>
                <div className="mt-0.5 font-medium text-stone-900">{it.title}</div>
                {it.description && <p className="mt-1 text-[14px] text-stone-600">{it.description}</p>}
                {it.location && <p className="mt-1 text-[12px] text-stone-500">{it.location}</p>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {needs.length > 0 && (
        <section className="mt-6 rounded-3xl border border-stone-200 bg-white p-6">
          <h2 className="font-serif text-xl">Your concierge will handle</h2>
          <ul className="mt-3 flex flex-wrap gap-2">
            {needs.map((n, i) => (
              <li key={i} className="rounded-full bg-stone-100 px-3 py-1 text-[13px] text-stone-700">{n}</li>
            ))}
          </ul>
        </section>
      )}

      {costs.length > 0 && (
        <section className="mt-6 rounded-3xl border border-stone-200 bg-white p-6">
          <h2 className="font-serif text-xl">Estimated breakdown</h2>
          <ul className="mt-3 divide-y divide-stone-100 text-[14px]">
            {costs.map((c, i) => (
              <li key={i} className="flex items-center justify-between py-2">
                <span className="text-stone-700">{c.item}</span>
                <span className="font-medium text-stone-900">${Number(c.amount).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Link
          to="/ai"
          data-testid="plan-detail-request-concierge"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-5 py-3 text-sm font-semibold text-white hover:bg-stone-800"
        >
          Request a Verified TREL Concierge →
        </Link>
        <Link
          to="/customer"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-5 py-3 text-sm font-semibold text-stone-800 hover:bg-stone-50"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

function MetaTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-stone-500">
        <Icon size={12}/> {label}
      </div>
      <div className="mt-1 text-sm font-medium text-stone-900">{value}</div>
    </div>
  );
}
